// The card, as a screen reads it: what a race row prints, what a chip counts,
// and what one race's runner table holds.
//
// `board-view.js` for racing, and deliberately its twin rather than its
// extension. The board's row is two teams and up to three cells; a race is a
// field of eight with a favourite, a jumped state, a scratching list and a
// per-book grid twelve columns wide, and threading all of that through
// `boardRow` would make one function that is two screens wearing a coat. What
// IS shared is every judgement: the fair price, the outlier, the covered
// sentence and the two copy builders all come from the same modules the Matches
// tab reads, so a race and a fixture cannot be told two different things about
// one price.
//
// PURE — no Vue, no DOM, no store and no clock of its own. A universe goes in
// and plain objects come out, which is what lets the whole of the Racing tab's
// arithmetic be tested without mounting a page.
//
// NOTHING HERE FETCHES. `raceUniverse` in store.js already holds the card the
// device was given, and `loadRacing` is the only thing that ever spends a
// credit on another one.

import {
  bestPrice, bestPriceToTake, buildUniverse, consensus, priceAt, selectionsOf,
} from '../engine/universe.js';
import { fromRaces } from './board.js';
import {
  arbsFor, exchangeTrustFor, findOutliers, outliersFor, RACING_MIN_BOOKS,
} from '../engine/outliers.js';
import { trustLine } from './trust.js';
import { attachRatings } from './ratings.js';
import { bookFairNet } from '../engine/models/sharpAnchor.js';
import { isRacingSport, raceKey, sportLabel } from '../engine/teams.js';
import { HAND_LABEL, localDate } from '../tickets.js';
import {
  racingBookmakerName, raceLabel, runnerId, runnerLabel, RACING_TZ, SPORT_FOR_CATEGORY,
} from './board.js';
// The card's own venue table, because a mover row carries the venue's NAME and
// a race is keyed on its id. `venueId` is where the sponsor comes off the front
// and the code off the back — "Sportsbet-Sandown-Hillside" and "Sandown
// Hillside" are one meeting, and a second slugger here would be a second answer
// to that (see HANDOFF §the match is four things).
import { venueId } from './match-exchange.js';

/**
 * A card as the engine reads it — the same three lines `raceUniverse` is in
 * store.js, and deliberately so.
 *
 * The store's computed is the one every screen reads; this is for the one case
 * the store has no card for and never will: a device with no PuntersEdge key,
 * where the Racing tab shows the SAMPLE card read-only so that somebody can see
 * what the tab is for before they go and get a key. Building it here keeps that
 * decision on the screen that makes it instead of putting a second board in the
 * store, and `sameDay: false` is the one option that matters — a card is "still
 * to jump today plus tomorrow morning", not a calendar day.
 */
export function universeFromCard(card, { now = Date.now(), features = null } = {}) {
  const asOf = card?.fetchedAt ?? new Date(now).toISOString();
  // The Hub's ratings ride onto the rows before the fold, exactly as they do in
  // the store's own `raceUniverse` — so a device with no key reads the sample
  // card through the same join a keyed one reads a real card through.
  return buildUniverse(attachRatings(fromRaces(card?.races ?? [], { asOf }), features), {
    forDate: card?.forDate ?? localDate(now, RACING_TZ),
    asOf,
    timeZone: RACING_TZ,
    sameDay: false,
  });
}

/**
 * The three codes, in the order the segment prints them — and the pref value
 * each one is remembered under.
 *
 * The CODE is the word on the chip ("horses"), not the sport id
 * (`racing_horse`): it is what `pref.racingCode` holds and what a query string
 * would carry, and a stored value that read `racing_greyhound` would be the
 * engine's vocabulary leaking into a preference somebody's phone keeps for a
 * year. One table, both directions.
 */
export const RACING_CODES = Object.freeze([
  // `noun` is the word the DATELINE counts in — "12 horse · 4 greyhound · 2
  // harness races" — which is singular and lower case because it is an
  // adjective on "races" there, where `label` is the word on the chip.
  { code: 'horses', sport: 'racing_horse', noun: 'horse' },
  { code: 'greyhounds', sport: 'racing_greyhound', noun: 'greyhound' },
  { code: 'harness', sport: 'racing_harness', noun: 'harness' },
]);

/** Where the chosen code is remembered. */
export const RACING_CODE_PREF = 'pref.racingCode';

/** Horses, because it is the code with a meeting on every day of the year. */
export const DEFAULT_CODE = 'horses';

/** `'horses'` → `'racing_horse'`, and an unknown code → the default's sport. */
export function sportForCode(code) {
  return (RACING_CODES.find((c) => c.code === code) ?? RACING_CODES[0]).sport;
}

/** `'racing_horse'` → `'horses'`, and anything that is not a code → null. */
export function codeForSport(sport) {
  return RACING_CODES.find((c) => c.sport === sport)?.code ?? null;
}

/**
 * The chip that is not a meeting: the races the detector has something to say
 * about. A value in the same segment as the venues, exactly as `MISPRICED` is a
 * value in the board's sport segment — one filter, never a filter and a switch
 * that can contradict each other.
 */
export const MISPRICED_MEETING = 'mispriced';

/**
 * The other chip that is not a meeting: the races with a runner the money has
 * come for. Same segment, same rule — one filter, never a filter and a switch.
 */
export const STEAMERS_MEETING = 'steamers';

/** Milliseconds, with anything unreadable sinking to the end of a sort. */
const ms = (iso) => {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
};

/** The matches in a universe, whether it is the store's Map or a bare list. */
function matchesOf(universe) {
  const source = universe?.matches ?? universe;
  if (!source) return [];
  if (typeof source.values === 'function') return [...source.values()];
  return Array.isArray(source) ? [...source] : [];
}

/** Every race on the card, whatever the caller had to hand. */
const racesOf = (universe) => matchesOf(universe).filter((m) => isRacingSport(m?.sport));

/**
 * HAS IT JUMPED? Three answers, and the feed's own two come first.
 *
 * `inplayAt` is the upstream saying the gates have opened and `marketClosedAt`
 * is a bookmaker saying it will not take another bet — both of them are facts
 * about the race, and both beat the phone's clock, which can be a minute or two
 * out and is a minute or two that matters on a race lasting seventy seconds.
 * The clock is the fallback for a card fetched before either field was set.
 *
 * NOT A CUT. A jumped race stays on the board, dimmed and stamped "Jumped",
 * because a punter who has just watched one wants to see it where they left it
 * rather than to find the list has silently shortened under their thumb.
 */
export function hasJumped(match, now = null) {
  if (match?.inplayAt) return true;
  const closed = ms(match?.marketClosedAt);
  const at = Number(now);
  if (!Number.isFinite(at)) return false;
  if (Number.isFinite(closed) && closed <= at) return true;
  return ms(match?.kickoff) <= at;
}

// --- movers: what the money did to a price ------------------------------------
//
/**
 * HOW FAR A PRICE HAS TO MOVE, AND HOW MANY BOOKS HAVE TO MOVE IT, before the
 * app says a runner firmed or drifted.
 *
 * Ten per cent on three books, which is exactly what `fetchMovers` asks
 * PuntersEdge for (`min_move_pct` / `min_books` in
 * server/providers/puntersEdge.mjs) — asked for there and asserted again here,
 * because the answer is filtered on this side rather than trusted: a feed that
 * loosened its own defaults would otherwise put an arrow on a runner whose
 * price went nowhere, and an arrow is a claim about money.
 *
 * ONE BOOK SHORTENING IS NOT A MOVE. It is one trader, and a card carries
 * fourteen of them; three moving the same way is the fewest that can be told
 * apart from one shop correcting a typo.
 */
export const MOVER_MIN_MOVE_PCT = 10;
export const MOVER_MIN_BOOKS = 3;

//
// A MOVER IS NOT A TIP AND THE APP MUST NOT LET IT READ AS ONE. Every figure
// here is a fact about a price — it was 11.00 this morning, it is 9.20 now, and
// nine books moved it — and the screens print exactly that and stop. There is no
// "money has come for this one", no strategy that reads a mover and no leg that
// carries one: the paper trail has never been asked whether a steamer wins, so
// the app has nothing honest to say about whether one does.
//
// THREE CREDITS, AND ONLY ON AN EXPLICIT REFRESH. `refreshRacing` in store.js is
// the only caller and it has to have been asked — never a tab enter, never a
// timer. Nothing in this file fetches; it reads what that call already bought.

/**
 * A number, or null — and `null`, `undefined` and `''` are NOT numbers.
 *
 * `Number(null)` is 0, which would turn a mover row with no race number into
 * race R0 and one with no cloth number into runner `r0`: two races that do not
 * exist, joined to nothing, with an arrow each.
 */
const finite = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
};

/** `-16.64` → `17`. The size of the move, without its sign or its decimals. */
const movePct = (n) => {
  const v = finite(n);
  return v === null ? null : Math.round(Math.abs(v));
};

/**
 * The books that moved THE SAME WAY, which is the only count that confirms
 * anything. `books_quoting` counts every shop with a price up, including the
 * twelve that did not budge.
 */
const confirming = (row) =>
  Number(row?.direction === 'firming' ? row?.books_firming : row?.books_drifting) || 0;

/**
 * Which race a mover row belongs to, as the card's own `matchKey`.
 *
 * Rebuilt from venue, code and race number rather than carried, because the
 * movers route is a PASS-THROUGH (server/racing.mjs) and a mover row has no
 * `race_id` a card row could be joined on. `raceKey` is the engine's own
 * expression of that key — the same function `fromRaces` keys a race with — so
 * the two cannot drift by a timezone or a hyphen.
 */
function moverKey(row) {
  const sport = SPORT_FOR_CATEGORY[String(row?.category ?? '').toLowerCase()];
  const venue = venueId(row?.venue);
  const number = finite(row?.race_number);
  if (!sport || !venue || number === null || !row?.start_time) return null;
  return raceKey(sport, row.start_time, venue, number);
}

/**
 * The movers worth printing, filed by race and then by runner.
 *
 * PURE, and the only reader of PuntersEdge's mover shape in the app. In go the
 * rows the route answered, out comes `Map<matchKey, Map<selection, mover>>` —
 * `selection` being `r4`, the same id the leg, the price column and the result
 * all use, so a mover lands beside the runner it is about without anything on a
 * screen matching on a name.
 *
 * Filtered again on this side (see `MOVER_MIN_MOVE_PCT` / `MOVER_MIN_BOOKS` in
 * board.js): a row that moved less than ten per cent, or that fewer than three
 * books moved, is not a mover here whatever the feed was asked for. A runner
 * with no cloth number is dropped — there is nothing to hang it on.
 */
export function moversByRace(movers, {
  minMovePct = MOVER_MIN_MOVE_PCT,
  minBooks = MOVER_MIN_BOOKS,
} = {}) {
  const byRace = new Map();
  for (const row of movers ?? []) {
    const direction = row?.direction;
    if (direction !== 'firming' && direction !== 'drifting') continue;
    const books = confirming(row);
    if (books < minBooks) continue;
    const moved = movePct(row?.move_pct);
    if (!Number.isFinite(moved) || moved < minMovePct) continue;
    const key = moverKey(row);
    const number = finite(row?.number);
    if (!key || number === null) continue;

    const selection = runnerId(number);
    const entry = {
      matchKey: key,
      selection,
      number,
      name: row?.runner ?? '',
      direction,
      // The MAGNITUDE, because the word beside it already carries the
      // direction: "firmed −17%" is the same fact said twice, once backwards.
      movePct: moved,
      books,
      openPrice: finite(row?.open_price),
      currentPrice: finite(row?.current_price),
    };
    const race = byRace.get(key) ?? new Map();
    // The FIRST row for a runner wins. The feed answers one row per runner per
    // race; two would be a race published twice, and the later one is not
    // evidence that the earlier was wrong.
    if (!race.has(selection)) race.set(selection, entry);
    byRace.set(key, race);
  }
  return byRace;
}

/** One runner's mover, or null. `byRace` is `moversByRace`'s answer. */
export function moverFor(byRace, matchKey, selection) {
  return byRace?.get?.(matchKey)?.get?.(selection) ?? null;
}

/** Has anything in this race firmed? What the Steamers chip counts. */
export function hasSteamer(byRace, matchKey) {
  const race = byRace?.get?.(matchKey);
  if (!race) return false;
  for (const mover of race.values()) if (mover.direction === 'firming') return true;
  return false;
}

/** `▲` on a runner the money came for, `▼` on one it left. */
export const moverMark = (mover) =>
  (mover ? (mover.direction === 'firming' ? '▲' : '▼') : '');

/** "firmed" / "drifted" — what happened, in one word, in the past tense. */
export const moverWord = (mover) =>
  (mover ? (mover.direction === 'firming' ? 'firmed' : 'drifted') : '');

/**
 * "firmed 12% (5 books)" — the whole of what the app says about a mover.
 *
 * The figure and the count and nothing else. A sentence about what the move
 * MEANS would be the app claiming knowledge it has never tested, and the books
 * are in the line because "firmed 12%" on one shop and on five are different
 * facts.
 */
export function moverLine(mover) {
  if (!mover) return '';
  return `${moverWord(mover)} ${mover.movePct}% (${mover.books} book${mover.books === 1 ? '' : 's'})`;
}

/** Is this race one the detector has anything to say about? */
const isMispriced = (match, now = null) => outliersFor(match, { now }).length > 0 || arbsFor(match) !== null;

/**
 * Every mispriced runner on the CARD, longest edge first.
 *
 * A pure function over `raceUniverse` rather than a computed in the store, and
 * that is the rule for everything this screen needs: the store owns when a card
 * is bought, this file owns what is made of it. Racing's own thresholds apply
 * per match inside `outliersFor` (`thresholdsFor(sport)`), so nothing here has
 * to know what they are.
 */
export function racingOutliers(universe, { now = null } = {}) {
  // `findOutliers`' own `now` is a kick-off cut, and racing's jumped rule is
  // wider than the clock (see `hasJumped`), so the races are cut here and the
  // detector is handed only the ones still to run.
  const live = racesOf(universe).filter((race) => !hasJumped(race, now));
  const byKey = new Map(live.map((race) => [race.matchKey, race]));
  // `race` is the LABEL, and it is added here rather than left to the caller
  // because `competition` on a racing match is the meeting ("Randwick
  // 2026-09-15") and nothing outside this folder knows that "Randwick R5" is
  // the thing a punter would go and look for. A notification is the one reader
  // that has no screen around it to say which race it means.
  // The clock goes with them: how long a race has to go is half of whether its
  // exchange price is the yardstick (`exchangeTrustFor`).
  return findOutliers(live, { now }).map((o) => ({ ...o, race: raceLabel(byKey.get(o.matchKey)) }));
}

/**
 * The code segment: Horses · Greyhounds · Harness, always all three and always
 * in that order.
 *
 * ALWAYS ALL THREE, unlike the board's sport chips, which are built from what
 * is on the board. A segment is a choice between fixed positions — a control
 * whose options came and went with the card would move under the thumb of
 * somebody flicking between codes at 3pm on a Saturday, and a punter who knows
 * the greyhounds are on wants to be told the card has none rather than to find
 * the chip missing. The count is what says so.
 */
export function categoryChips(universe, { now = null } = {}) {
  const counts = new Map();
  const toRun = new Map();
  for (const race of racesOf(universe)) {
    counts.set(race.sport, (counts.get(race.sport) ?? 0) + 1);
    if (!hasJumped(race, now)) toRun.set(race.sport, (toRun.get(race.sport) ?? 0) + 1);
  }
  return RACING_CODES.map(({ code, sport }) => ({
    code,
    sport,
    label: sportLabel(sport),
    // WHAT THE LIST BELOW PRINTS, which is every race of that code, jumped ones
    // included — a chip reading 4 over a list of 6 is a screen arguing with
    // itself. `toRun` is the same count without them, for anything that wants
    // to know whether the code has anything left today.
    count: counts.get(sport) ?? 0,
    toRun: toRun.get(sport) ?? 0,
  }));
}

/**
 * The meeting chips: one per venue on the chosen code, busiest first, with
 * "Mispriced N" at the head when the detector has found anything.
 *
 * Busiest first for `sportChips`'s own reason — the chip somebody is most
 * likely to want is the one nearest the "All" they start from, and a scrollable
 * strip puts everything after the first three out of sight. A Saturday carries
 * six meetings on the gallops alone.
 */
export function meetingChips(universe, { code = DEFAULT_CODE, now = null, movers = null } = {}) {
  const sport = sportForCode(code);
  const byVenue = new Map();
  let flagged = 0;
  let steamers = 0;

  for (const race of racesOf(universe)) {
    if (race.sport !== sport) continue;
    const id = race.venueId ?? race.venue ?? '';
    const entry = byVenue.get(id) ?? { venueId: id, label: race.venue ?? id, count: 0 };
    entry.count += 1;
    byVenue.set(id, entry);
    if (hasJumped(race, now)) continue;
    if (isMispriced(race, now)) flagged += 1;
    // A RACE THAT HAS JUMPED HAS NO STEAMER, whatever its price did. The chip
    // is a way of finding something still to bet on; a move on a race that has
    // been run is a fact about this morning.
    if (movers && hasSteamer(movers, race.matchKey)) steamers += 1;
  }

  const head = [];
  if (flagged > 0) head.push({ venueId: MISPRICED_MEETING, label: 'Mispriced', count: flagged });
  if (steamers > 0) head.push({ venueId: STEAMERS_MEETING, label: 'Steamers', count: steamers });

  return head.concat([...byVenue.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)));
}

/**
 * The small print under a runner's name: `B3 · J McDonald · 57kg · form 1-1-2`.
 *
 * Every field is optional and a missing one is simply absent rather than
 * printed as a dash — a greyhound has no jockey and no weight, and a row of
 * em dashes across a whole card would be the app saying "unknown" eight times
 * about a thing that does not exist.
 */
export function runnerSmallPrint(runner) {
  return [
    runner?.barrier === null || runner?.barrier === undefined ? null : `B${runner.barrier}`,
    runner?.jockey ?? null,
    runner?.weight === null || runner?.weight === undefined ? null : `${runner.weight}kg`,
    runner?.form ? `form ${runner.form}` : null,
  ].filter(Boolean).join(' · ');
}

/** The market's fair PRICE for a runner — `board-view.js`'s own arithmetic. */
function fairPrice(match, selection) {
  const market = consensus(match, selection);
  return market && market.pFair > 0 ? 1 / market.pFair : null;
}

/** price / fair − 1, and null rather than zero when there is no fair to beat. */
const edgeOf = (price, fair) =>
  (fair !== null && Number.isFinite(price) ? price / fair - 1 : null);

/**
 * THE EXCHANGE'S OWN FAIR PRICE, NET OF COMMISSION.
 *
 * `bookFairNet` is the one place in the app that reads a Betfair price as what
 * it actually pays — the back price deflated by commission, then devigged on
 * the exchange's own book — and the Match page's sharp anchor reads it from
 * there too. A second deflation here would be a second answer to one question,
 * and it would be 5% of the profit out.
 *
 * Null when the exchange is not on this race: the phone has no Betfair session,
 * the market did not match, or the web, where there is no route to Betfair at
 * all (see HANDOFF §Betfair).
 */
export const EXCHANGE = 'betfair_ex_au';

export function exchangeFair(match, selection) {
  const p = bookFairNet(match, selection, EXCHANGE);
  return p > 0 ? 1 / p : null;
}

/** How many races on this card carry an exchange price at all. */
export function exchangeRaces(universe) {
  return racesOf(universe).filter((race) =>
    selectionsOf(race).some((s) => priceAt(race, s, EXCHANGE) !== null)).length;
}

// --- a race too thin to have a fair price -------------------------------------
//
// THE BUG THIS EXISTS FOR, seen on a live card (2026-09-15): a Menangle trot
// that ONE book had priced showed "−11.2% vs fair" down to "−67.2% vs fair" on
// every runner. The "fair" was TAB's own vector devigged against itself — a 132%
// round scaled back to 100% — so every figure on the screen was the app telling
// a punter how far TAB's price was from TAB's price, in the app's action ink, on
// a race where it knew nothing at all. The tail of a single-book market is where
// all of that book's margin is loaded, which is why the figures got worse the
// longer the price.
//
// THE RULE: a runner's fair price never comes from a market that is one book, and
// never from the same book being judged. Fewer than `RACING_MIN_BOOKS` shops on
// the race (the exchange is not a shop) and there is no market fair — the
// exchange takes over where it is there, net of commission, and where it is not
// the figure comes off the screen and the screen says why. It is the detector's
// own floor, so a race the app will not flag is a race it will not judge either.

/** The bookmakers pricing this race, the exchange excluded, in first-seen order. */
function racingBooksOn(match) {
  const seen = [];
  for (const selection of selectionsOf(match)) {
    for (const row of match?.prices?.[selection] ?? []) {
      if (row.bookmaker === EXCHANGE || !Number.isFinite(row.price)) continue;
      if (!seen.includes(row.bookmaker)) seen.push(row.bookmaker);
    }
  }
  return seen;
}

/**
 * One runner's fair price and where it came from.
 *
 * Three answers, in order: the detector's own excluded-book fair on a flagged
 * runner, the market's where enough shops are voting, the exchange's where they
 * are not — and null, which is a screen with no percentage on it.
 *
 * `consensus(..., { exclude: EXCHANGE })` is how the votes are counted, and it
 * is the same count `outliersFor` gates on. The exchange is a voter in the fair
 * PRICE (it always was) and is deliberately not one in the count: a race only
 * Betfair and one shop have touched is not a market of two opinions.
 */
function fairFor(match, selection, { flagged = null, thin = false } = {}) {
  if (flagged) return { fair: flagged.fair, fairSource: 'market' };
  const voters = consensus(match, selection, { exclude: EXCHANGE })?.books ?? 0;
  if (!thin && voters >= RACING_MIN_BOOKS) {
    return { fair: fairPrice(match, selection), fairSource: 'market' };
  }
  const exchange = exchangeFair(match, selection);
  if (exchange !== null) return { fair: exchange, fairSource: 'exchange' };
  return { fair: null, fairSource: null };
}

/** "TAB", "TAB and Neds", "TAB, Neds and Sportsbet". */
function namesPhrase(names) {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Why there is no percentage beside these runners, in one sentence, naming the
 * shops that ARE on the race — because "not enough books" reads as a fault in
 * the app, and "only TAB has priced this race yet" reads as what it is: a
 * country meeting the rest of the market has not got to.
 */
export function thinMarketNote(books, hasExchange = false) {
  const names = books.map(racingBookmakerName);
  const who = names.length
    ? `Only ${namesPhrase(names)} ${names.length === 1 ? 'has' : 'have'} priced this race yet`
    : 'No bookmaker has priced this race yet';
  return hasExchange
    ? `${who}. Fair here is the Betfair exchange, net of commission.`
    : `${who}. Fair needs ${RACING_MIN_BOOKS} books (or the exchange).`;
}

/**
 * WHAT IS ON THE CARD, BY CODE: "12 horse · 4 greyhound · 2 harness races".
 *
 * THE COUNTS LIVE HERE BECAUSE THEY CANNOT LIVE ON THE SEGMENT. R3 put them on
 * the chips and took them off again the same afternoon: three fixed positions
 * share the width of a phone and "Greyhounds · 1" truncates to "Greyhoun…" at
 * 375, which loses the name of the code to say a number. The dateline is a
 * wrapping line of small print with room for all three, and it is already where
 * this screen says what it is holding.
 *
 * A code with nothing on it is LEFT OUT rather than printed as a zero — the
 * segment above still carries all three words, so a punter who wants the dogs
 * can see the chip and find the list empty, which is the honest order of those
 * two facts. Takes `categoryChips`' own answer, so the counts in the line and
 * the counts behind the chips are one derivation.
 */
export function codeCounts(chips) {
  const on = (chips ?? []).filter((c) => c.count > 0);
  if (!on.length) return 'no races';
  const said = on.map((c) =>
    `${c.count} ${RACING_CODES.find((r) => r.sport === c.sport)?.noun ?? c.code}`);
  // "races" for the whole line, singular only where the whole line is one race.
  const one = on.length === 1 && on[0].count === 1;
  return `${said.join(' · ')} race${one ? '' : 's'}`;
}

/**
 * The dateline: "Prices from PuntersEdge at 9:11 am · 12 horse · 4 greyhound ·
 * 2 harness races · Betfair fair on 18 of 24 races".
 *
 * `at` arrives already written, because the clock's formatting belongs to the
 * page (`format.js`'s `time`) and this file keeps no timezone of its own. The
 * exchange half is left off entirely where nothing matched — on the web that is
 * every card, and a permanent "Betfair fair on 0 of 24" would be a daily
 * reminder of a thing that cannot work there.
 */
export function racingDateline({
  at = null, races = 0, exchange = 0, sample = false, counts = null,
} = {}) {
  const tally = counts ? codeCounts(counts) : `${races} race${races === 1 ? '' : 's'}`;
  if (sample) return `Sample card · ${tally}`;
  return [
    at ? `Prices from PuntersEdge at ${at}` : null,
    tally,
    exchange > 0 ? `Betfair fair on ${exchange} of ${races} races` : null,
  ].filter(Boolean).join(' · ');
}

/** The favourite: the shortest price a punter could actually take. */
function favouriteOf(match) {
  let best = null;
  for (const selection of selectionsOf(match)) {
    const take = bestPriceToTake(match, selection) ?? bestPrice(match, selection);
    if (!take) continue;
    if (!best || take.price < best.price) {
      best = { selection, price: take.price, name: match.runners?.[selection]?.name ?? selection };
    }
  }
  return best;
}

/**
 * One race as a board ROW: which race it is, where and over what, the favourite,
 * and whatever the detector has to say.
 *
 * Flat and already resolved, for `board-view.js`'s own reason: the page renders
 * this straight, and a row that made the template walk back into `match.prices`
 * would do the same lookup once per cell per render on the one screen holding a
 * whole Saturday.
 */
function raceRow(match, now, movers) {
  const outliers = outliersFor(match, { now }).map((o) => ({
    ...o,
    bookmakerName: racingBookmakerName(o.bookmaker),
  }));
  const arb = arbsFor(match);
  const runners = selectionsOf(match).length;
  const favourite = favouriteOf(match);
  return {
    key: match.matchKey,
    sport: match.sport,
    venue: match.venue ?? '',
    venueId: match.venueId ?? '',
    raceNumber: match.raceNumber ?? null,
    raceName: match.raceName ?? null,
    kickoff: match.kickoff,
    // "R5" — the number a punter says out loud, beside the time.
    number: match.raceNumber === null || match.raceNumber === undefined
      ? '' : `R${match.raceNumber}`,
    // "Randwick · 1200m · Good 4 · 12 runners". Distance and going are missing
    // often enough on a country card that they are joined rather than
    // templated: a line reading "· · 8 runners" is a row with holes in it.
    meta: [
      match.venue ?? '',
      match.distance ? `${match.distance}m` : null,
      match.trackCondition ?? null,
      `${runners} runner${runners === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' · '),
    runners,
    favourite,
    // THE FAVOURITE'S MOVE, and only the favourite's: the row has one figure of
    // room and the shortest price is the one the row already names. Every other
    // runner's move is on the race's own page.
    mover: favourite ? moverFor(movers, match.matchKey, favourite.selection) : null,
    // Whether ANY runner in the race firmed — which is what the Steamers chip
    // filters on, and it is deliberately not the same question as the line
    // above: a race whose second favourite has come in is a steamer race with
    // nothing on its row.
    steamer: hasSteamer(movers, match.matchKey),
    outliers,
    arb: arb ? { ...arb, legs: arb.legs.map((l) => ({ ...l, bookmakerName: racingBookmakerName(l.bookmaker) })) } : null,
    mispriced: outliers.length > 0 || arb !== null,
    jumped: hasJumped(match, now),
  };
}

/**
 * The card as rows, in START ORDER, with the jumped ones first.
 *
 * START ORDER AND NOTHING ELSE — no grouping by meeting, which is the one thing
 * a racing screen must not do: what a punter wants at 2:38 is the race about to
 * jump, and a list sectioned by venue hides it three headings down. The meeting
 * is a chip above instead.
 *
 * Jumped first because the Now rule sits between the two halves, exactly as it
 * does on Today: everything above it has gone, everything below is still to
 * come. They are already in that order by the clock; sorting on the flag as
 * well is what keeps a race the FEED has called in-play above the rule even
 * when the phone thinks it has a minute to go.
 */
export function raceRows(universe, {
  code = DEFAULT_CODE,
  meeting = null,
  now = null,
  mispricedOnly = false,
  movers = null,
} = {}) {
  const sport = sportForCode(code);
  const special = meeting === MISPRICED_MEETING || meeting === STEAMERS_MEETING;
  const wantVenue = meeting && meeting !== 'all' && !special ? meeting : null;
  const onlyFlagged = mispricedOnly || meeting === MISPRICED_MEETING;
  const onlySteamers = meeting === STEAMERS_MEETING;

  const rows = [];
  for (const race of racesOf(universe)) {
    if (race.sport !== sport) continue;
    if (wantVenue && (race.venueId ?? race.venue) !== wantVenue) continue;
    const row = raceRow(race, now, movers);
    if (onlyFlagged && !(row.mispriced && !row.jumped)) continue;
    if (onlySteamers && !(row.steamer && !row.jumped)) continue;
    rows.push(row);
  }

  return rows.sort((a, b) =>
    Number(b.jumped) - Number(a.jumped)
    || ms(a.kickoff) - ms(b.kickoff)
    || String(a.venue).localeCompare(String(b.venue)));
}

/** How many of these rows have jumped — where the Now rule goes. */
export const foldOf = (rows) => (rows ?? []).filter((r) => r.jumped).length;

/**
 * One race as the RUNNER TABLE: a row per active runner, the scratchings under
 * it, and the whole per-book grid behind the disclosure.
 *
 * SHORTEST PRICE FIRST, not cloth number. The number is printed on every row so
 * nobody has to hunt for it, and the question this screen answers is "who do
 * the books think wins, and is anybody out of line about it" — which is a
 * question about the market's order, not the clerk's. Scratchings keep their
 * own order at the foot.
 *
 * `bookmakers` is the order the per-book grid's rows come out in; entries may
 * be `{ key, name }` or a bare key, exactly as `matchSheet` takes them. A book
 * pricing nothing on this race is left out rather than printed as a row of
 * dashes.
 */
export function raceSheet(match, bookmakers = [], { movers = null, now = null } = {}) {
  if (!match) {
    return {
      title: '',
      metaParts: [],
      runners: [],
      scratched: [],
      grid: { runners: [], rows: [] },
      outliers: [],
      arb: null,
      exchange: false,
      books: [],
      thin: false,
      rated: false,
      thinNote: '',
      trust: null,
      trustNote: '',
    };
  }

  // WHICH YARDSTICK THIS RACE IS BEING READ AGAINST, once, for the page as well
  // as for the detector: the exchange where the money has arrived, the local
  // books where it has not. `outliersFor` asks the same question of the same
  // clock, so the sentence under the field and the figures in it cannot
  // disagree.
  const trust = exchangeTrustFor(match, { now });
  const outliers = outliersFor(match, { now }).map((o) => ({
    ...o,
    bookmakerName: racingBookmakerName(o.bookmaker),
  }));
  const arb = arbsFor(match);

  // HOW MANY SHOPS ARE ACTUALLY ON THIS RACE, the exchange not among them.
  const books = racingBooksOn(match);
  const thin = books.length < RACING_MIN_BOOKS;

  const runners = selectionsOf(match)
    .filter((s) => (match.prices?.[s]?.length ?? 0) > 0 || match.runners?.[s])
    .map((selection) => {
      const runner = match.runners?.[selection] ?? {};
      const take = bestPriceToTake(match, selection) ?? bestPrice(match, selection);
      const price = take?.price ?? null;
      // The detector's figure REPLACES the cell's on a flagged runner, exactly
      // as `matchSheet`'s footer does it: a fair price the flagged book itself
      // voted on is a fair price it has already moved, and only the excluded
      // form is ever printed as a percentage.
      const flagged = outliers.find((o) => o.side === selection && o.bookmaker === take?.bookmaker) ?? null;
      const { fair, fairSource } = fairFor(match, selection, { flagged, thin });
      return {
        selection,
        number: runner.number ?? null,
        name: runner.name ?? selection,
        smallPrint: runnerSmallPrint(runner),
        price,
        bookmaker: take?.bookmaker ?? null,
        bookmakerName: take ? racingBookmakerName(take.bookmaker) : '',
        fair,
        edge: flagged ? flagged.edge : edgeOf(price, fair),
        // WHAT THAT FAIR PRICE IS, in one word, because the row has to say which
        // yardstick it is measuring against: 'market' | 'exchange' | null.
        fairSource,
        exchangeFair: exchangeFair(match, selection),
        outlier: flagged,
        // THE HUB'S OWN RATED PRICE FOR THIS RUNNER, or null. A second opinion
        // and nothing more: it is printed in the quiet ink under the name and
        // no figure on this row is measured against it. `rating` rides on the
        // runner through `RACE_FIELDS` (lib/racing/ratings.js).
        rating: runner.rating ?? null,
        // What the money did to this runner's price since the card opened, or
        // null — which is what it is on every runner on a device that has not
        // bought the movers, and on most runners on one that has.
        mover: moverFor(movers, match.matchKey, selection),
        scratched: false,
      };
    })
    .sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity)
      || (a.number ?? 0) - (b.number ?? 0));

  // THE SCRATCHINGS ARE NOT SELECTIONS and never were — they are in `runners`
  // carrying `scratched: true` and nowhere else, which is what keeps a book
  // still quoting one out of the consensus (see lib/racing/board.js).
  const scratched = Object.entries(match.runners ?? {})
    .filter(([, r]) => r?.scratched)
    .map(([selection, r]) => ({
      selection,
      number: r.number ?? null,
      name: r.name ?? selection,
      smallPrint: runnerSmallPrint(r),
      scratched: true,
    }))
    .sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  const order = runners.map((r) => r.selection);
  const rows = [];
  for (const entry of bookmakers ?? []) {
    const key = typeof entry === 'string' ? entry : entry?.key;
    if (!key) continue;
    const prices = order.map((s) => priceAt(match, s, key));
    if (prices.every((p) => p === null)) continue;
    rows.push({ bookmaker: key, bookmakerName: racingBookmakerName(key), prices });
  }

  return {
    title: raceLabel(match),
    // The time is the page's to write — `format.js`'s `time` is the only place
    // a clock is formatted in this app, and it takes the reader's own zone.
    metaParts: [
      match.distance ? `${match.distance}m` : null,
      match.raceName ?? null,
      match.trackCondition ?? null,
      `${runners.length} runner${runners.length === 1 ? '' : 's'}`,
    ].filter(Boolean),
    runners,
    scratched,
    grid: { runners: runners.map((r) => ({ selection: r.selection, number: r.number, name: r.name })), rows },
    outliers,
    arb,
    // WHETHER THERE IS AN EXCHANGE COLUMN AT ALL. On the web there never is
    // (Betfair refuses Cloud Run, see HANDOFF), and on a phone without a
    // session there is not either — a column of dashes headed "Betfair fair" is
    // a promise the app cannot keep on that device, so the column comes off.
    exchange: runners.some((r) => r.exchangeFair !== null),
    // The shops on the race, named, and whether there are too few of them to
    // make a market. The page prints `thinNote` under the field.
    books: books.map(racingBookmakerName),
    thin,
    // Does anything in this field carry the Hub's rating? What the page turns
    // its one "not a tip" sentence on.
    rated: runners.some((r) => r.rating),
    thinNote: thin
      ? thinMarketNote(books, runners.some((r) => r.fairSource === 'exchange'))
      : '',
    // THE GATE'S OWN ANSWER, and the sentence for it. Printed only where the
    // exchange is on the race at all: a chip about Betfair on a race Betfair is
    // not pricing is a fact about a market nobody on this device can see.
    trust,
    trustNote: runners.some((r) => r.exchangeFair !== null)
      ? trustLine(trust, { totalMatched: trust.totalMatched, minutesToJump: trust.minutesToJump })
      : '',
  };
}

/**
 * Every book pricing one runner, for the log sheet's picker: `{ key, name,
 * price }`, longest price first.
 *
 * The exchange is NOT on the list. `bestPriceToTake` leaves it out of the best
 * price for the reason `universe.js` gives — somebody else's standing offer is
 * not a price a punter can walk in and take — and a shop somebody could not
 * have placed the bet at has no business being a choice on the slip that says
 * where they placed it.
 */
export function booksFor(match, selection) {
  return (match?.prices?.[selection] ?? [])
    .filter((row) => row.bookmaker !== EXCHANGE && Number.isFinite(row.price))
    .map((row) => ({ key: row.bookmaker, name: racingBookmakerName(row.bookmaker), price: row.price }))
    .sort((a, b) => b.price - a.price);
}

const cents = (n) => Math.round(Number(n) * 100) / 100;

/**
 * ONE LEG, OFF A RUNNER ON THE CARD — `legFromMatch`'s shape, written for a
 * race.
 *
 * WHY IT IS HERE AND NOT IN `lib/tickets.js`: at the commit this landed on,
 * `legFromMatch` read `match[side].name` and `match.home`/`match.away`, so a
 * leg built from a race came out as "` to win`" on "` v `". Teaching it about
 * runners is R4's line of the plan — it owns tickets.js and lands the racing
 * strategies that print these legs by the dozen — and doing it from here would
 * have been two slices editing one function on the same afternoon.
 *
 * SO IT IS WRITTEN TO BE THE SAME OBJECT, FIELD FOR FIELD: the same
 * "Winx (4) to win", the same "Randwick R5" (never with the distance on it, so
 * a bet logged by hand and a leg the engine picked print one string in
 * History), the same `side` settlement compares. When R4's `legFromMatch`
 * lands, this becomes a call to it and `runnerTicket` below becomes
 * `handTicket` — and nothing on any screen moves.
 *
 * `sportTitle` is the CODE — "Horses" — and not the competition, because for a
 * race the competition is the meeting ("Randwick 2026-09-15") and the meeting
 * is already said by `match` beside it.
 */
export function runnerLeg(match, selection, { price, bookmakerKey = null, fairProbability = null } = {}) {
  const odds = cents(Number(price));
  return {
    matchKey: match?.matchKey ?? null,
    eventId: match?.matchKey ?? null,
    side: selection,
    selection: `${runnerLabel(match, selection)} to win`,
    match: match?.raceLabel ?? raceLabel(match),
    sport: match?.sport ?? null,
    sportTitle: match?.sportTitle ?? (match?.sport ? sportLabel(match.sport) : null),
    // `commenceTime` and not `kickoff`: it is what `resolveLeg` dates a leg by
    // and what every other leg in the app carries.
    commenceTime: match?.kickoff ?? null,
    price: odds,
    impliedProbability: 1 / odds,
    fairProbability,
    bookmakerKey,
  };
}

/**
 * A bet somebody placed on a runner, shaped as a ticket the ledger can hold —
 * `handTicket` for a race, and the same refusals in the same words.
 *
 * It goes into `markPlaced` and nowhere else, which is the rule the whole
 * ledger rests on: the sample board, the break and the stake limits are all
 * enforced there and only there, so a second way in would be a way round all
 * three. See `runnerLeg` above for why this is not `handTicket` itself yet.
 */
export function runnerTicket({
  match,
  selection,
  price,
  bookmakerKey = null,
  bookmaker = null,
  stake,
  fairProbability = null,
  timeZone = RACING_TZ,
  now = Date.now(),
}) {
  const matchKey = match?.matchKey ?? null;
  if (!matchKey || !selection) throw new Error('That race is no longer on the card.');
  const odds = Number(price);
  if (!Number.isFinite(odds) || odds <= 1) throw new Error('Enter the price you took, like 4.20.');
  const staked = Number(stake);
  if (!Number.isFinite(staked) || staked <= 0) throw new Error('Enter what you put on, like 10.');

  const combinedOdds = cents(odds);
  const amount = cents(staked);
  const id = `hand-${matchKey}-${selection}-${now}`;
  const kickoff = match?.kickoff ?? null;

  return {
    multi: {
      id,
      tier: null,
      // The MEETING's own Sydney day, not the day it was typed in: a race at
      // 8pm Perth time is still the Saturday it was carded on.
      forDate: kickoff ? localDate(kickoff, timeZone) : localDate(now, timeZone),
      status: 'proposed',
      strategyId: null,
      // The same frozen label a hand-logged single off the board carries, from
      // the same constant: History names both rows the same way or it is two
      // ledgers.
      label: HAND_LABEL,
      stake: amount,
      bookmaker,
      bookmakerKey,
      oddsFetchedAt: new Date(now).toISOString(),
      combinedOdds,
      combinedProbability: 1 / combinedOdds,
      potentialReturn: cents(amount * combinedOdds),
    },
    legs: [{
      id: `${id}-0`,
      multiId: id,
      ...runnerLeg(match, selection, { price: combinedOdds, bookmakerKey, fairProbability }),
    }],
  };
}
